/* eslint-disable ember/no-runloop */
import type Store from "@ember-data/store";
import type ModelRegistry from "@ember-data/types/registries/model";
import { assert } from "@ember/debug";
import { next } from "@ember/runloop";
import { service } from "@ember/service";
import { dasherize } from "@ember/string";
import { Changeset } from "ember-changeset";
import lookupValidator from "ember-changeset-validations";
import type {
  BufferedChangeset,
  ValidatorAction,
  ValidatorMap
} from "ember-changeset/types";
import type FlashMessagesService from "ember-cli-flash/services/flash-messages";
import ForcBaseComponent from "parog-web/components/forc/abstract/base-component";
import DraftManager from "parog-web/components/forc/draft-manager";
import FormAction from "parog-web/components/forc/form-action";
import type BioExamPrescribedJournalEvent from "parog-web/models/bio-exam-prescribed-journal-event";
import type BioExamResultsReceivedJournalEvent from "parog-web/models/bio-exam-results-received-journal-event";
import type ConsultationReportWrittenJournalEvent from "parog-web/models/consultation-report-written-journal-event";
import type DpCycleDeclaredJournalEvent from "parog-web/models/dp-cycle-declared-journal-event";
import type File from "parog-web/models/file";
import type FileAttachment from "parog-web/models/file-attachment";
import type FileUploadedJournalEvent from "parog-web/models/file-uploaded-journal-event";
import type JournalEventModel from "parog-web/models/journal-event";
import type MedicalEventJournalEvent from "parog-web/models/medical-event-journal-event";
import type MedicalImagingPrescribedJournalEvent from "parog-web/models/medical-imaging-prescribed-journal-event";
import type MedicationPrescribedJournalEvent from "parog-web/models/medication-prescribed-journal-event";
import type ParamedicalCarePrescribedJournalEvent from "parog-web/models/paramedical-care-prescribed-journal-event";
import type Patient from "parog-web/models/patient";
import emberSwal from "ui-kit/utils/ember-swal";
import type { ForcBaseArgs } from "./base-component";

export interface ToastMessage {
  title?: string;
  message: string;
}

export interface Payload extends ForcModelPojo {
  fileAttachments?: FileAttachment[];
  patient?: Patient | null;
}

export type ForcModelPojo = Record<string, unknown>;

export default class ForcJournalEventBasedComponent<
  Args extends ForcBaseArgs = ForcBaseArgs
> extends ForcBaseComponent<Args> {
  @service declare flashMessages: FlashMessagesService;
  @service requestManager;

  /**
   * Need to be overwrite by the FORC if we want a custom flash message
   * If you won't a flash message, overwrite it and return null
   *
   */
  get toastMessage(): ToastMessage | null {
    return {
      message: "Information bien enregistrée. Merci !"
    };
  }

  static hasDraftForPatient(patient: Patient): boolean {
    return DraftManager.hasDraftFor(
      DraftManager.localStorageKey(this.fullComponentId, patient)
    );
  }

  /**
   * The full component id, containing the feature name and the component name,
   * for instance "online-consultation/report-edition-form".
   * Ideally, this should be injected by Ember CLI, or the registry, but there
   * is no registry for components, component names are dynamically resolved at
   * runtime when instantiated.
   *
   * Thus, this must be set by developers when inheriting from this class.
   */
  static fullComponentId: string;

  get fullComponentId(): string {
    const fullComponentId = (
      this.constructor as typeof ForcJournalEventBasedComponent
    ).fullComponentId;
    assert(
      `The 'fullComponentId' is not defined for the class '${this.constructor.name}`,
      Boolean(fullComponentId)
    );
    return fullComponentId;
  }

  /**
   * The corresponding model name.
   * TODO: Typescript JournalEventClass generator
   */
  static journalEventClass;

  get journalEventClass(): any {
    return (this.constructor as typeof ForcJournalEventBasedComponent)
      .journalEventClass;
  }

  get journalEventClassType(): keyof ModelRegistry {
    return this.journalEventClass.type;
  }

  /**
   * This property is used to display the forc display name in the timeline or other places,
   * through formWithId hook in parogXX/addon/index.js
   * At launch, the `forc-registration` instance-initializer automatically find and add those forc components
   * to staticForms, so that they can be retrieved as usual forms
   *
   * Thus, you must define this property if you want your forc to be reachable from formWithId.
   */
  static forcDisplayName: string;

  get forcDisplayName(): string {
    return (this.constructor as typeof ForcJournalEventBasedComponent)
      .forcDisplayName;
  }

  @service declare store: Store;

  /// PRIVATE PROPERTIES

  get model(): ForcModelPojo | JournalEventModel {
    const model = this.retrieveModel();

    // This is the funny memoization pattern.
    // We can't do "delete this.model" because the accessor is defined in the prototype.
    // So we just have to set the property in the instance like this.
    Object.defineProperty(this, "model", {
      value: model,
      writable: false // if one day we need to set the model somewhere else we can set to true
    });

    return this.model;
  }

  afterLoad(): void {
    return;
  }

  get changeset(): BufferedChangeset {
    const changeset = this.computeChangeset();

    // Memoization :
    Object.defineProperty(this, "changeset", {
      value: changeset,
      writable: false
    });

    this.draft = new DraftManager(this);
    // The load of the draftManager must be called after the "memoization" is done.
    // `this.computeChangeset` creates a Changeset object and consumes its @tracked `_changes` property
    // calling `this.draft.load();` will call changeset.restore and will modified the @tracked `_changes` property
    // This can't be done in the same runloop or you'll get the error
    next(this, function () {
      this.draft.load();
      this.afterLoad();
    });

    return this.changeset;
  }

  get defaultModel() {
    return {};
  }

  get sourceJeId() {
    return this.args.sourceJeId;
  }

  /// USABLE INTERFACE

  /**
   * @type {DraftManager}
   * @private
   */
  draft;

  /**
   * An Ember Changeset Validations object
   */
  get validations(): ValidatorMap {
    Object.defineProperty(this, "validations", {
      value: {}
    });

    return this.validations;
  }

  /**
   * An Ember Changeset validator.
   * If set, it is used instead of the default `lookupValidator(this.validations)`.
   */
  validator?: ValidatorAction;

  /**
   * Describes the journal event's properties that will be used for load and save.
   */
  journalEventProperties: Array<string> = [];

  /**
   * If `true`, the content of the changeset is regularly saved in the local
   * storage, and automatically injected when the component is loaded in `new`
   * mode with the same patient and form id.
   */
  autoSaveDraft = false;

  get sourceJournalEvent(): JournalEventModel | null {
    if (this.sourceJeId) {
      const je = this.store.peekRecord(
        this.journalEventClassType,
        this.sourceJeId
      ) as JournalEventModel;
      if (je) {
        assert(
          "The patient should be the same as the one in the component.",
          je.patient?.get("id") == this.patient?.id
        );
        return je;
      }
      return null;
    }
    return null;
  }

  /**
   * Describes the strategy used to retrieve the Journal Event that will be used as a model.
   * Default strategies can be found in `journal-event-retrieval-strategies`.
   *
   */
  retrieveJournalEvent(): JournalEventModel | undefined {
    return;
  }

  get retrievedJournalEvent(): JournalEventModel | undefined {
    return this.retrieveJournalEvent();
  }

  /// STATE

  get isEditMode(): boolean {
    return this.formAction == FormAction.Edit;
  }

  get isNewMode(): boolean {
    // We suppose that if we do not have formAction provided we are in new mode.
    return this.formAction == FormAction.New || this.formAction == null;
  }

  /**
   * Will be set by the 'retrieveModel' method if this form has been loaded
   * from an existing je.
   * TODO: Actually this is based on the 'retrievedJournalEvent' accessor which
   *       make a request to the store each time this is called.
   *       We should instead store the retrievedJe or store this in a variable.
   */
  get isLoadedFromJe(): boolean {
    return Boolean(this.retrievedJournalEvent);
  }

  /// IMPLEMENTATION

  async isValid(): Promise<boolean> {
    await this.changeset.validate();
    return this.changeset.get("isValid");
  }

  get isDirty(): boolean {
    const chatMessageKeys = ["chatMessage", "willSendMessage"];
    return this.changeset.changes.length == 2 &&
      chatMessageKeys.includes(this.changeset.changes[0].key) &&
      chatMessageKeys.includes(this.changeset.changes[1].key)
      ? false
      : this.changeset.get("isDirty");
  }

  /**
   * The model will be mutated so do not return a JournalEvent from the store but a copy!
   * By default return the last JournalEvent of the given type.
   * This method is only called once when the model is accessed for the first time.
   */
  retrieveModel(): ForcModelPojo {
    const journalEvent = this.retrievedJournalEvent || this.defaultModel;
    return this.journalEventProperties.reduce((acc, property) => {
      acc[property] = journalEvent[property];
      return acc;
    }, {});
  }

  /**
   * Compute the changeset based on the current model.
   * This method is only called once when the changeset is accessed for the first time.
   * It's possible to override it to add behavior to the changeset.
   */
  computeChangeset(): BufferedChangeset {
    const changeset = Changeset(
      this.model,
      this.validator ? this.validator : lookupValidator(this.validations),
      this.validations
    );

    changeset.on("afterValidation", () => {
      this.draft.onDidUpdate();
    });

    return changeset;
  }

  async saveWithFiles(
    journalEventClassType: keyof ModelRegistry = this.journalEventClassType
  ): Promise<
    | BioExamResultsReceivedJournalEvent
    | ConsultationReportWrittenJournalEvent
    | FileUploadedJournalEvent
    | BioExamPrescribedJournalEvent
    | MedicationPrescribedJournalEvent
    | MedicalImagingPrescribedJournalEvent
    | ParamedicalCarePrescribedJournalEvent
    | MedicalEventJournalEvent
    | DpCycleDeclaredJournalEvent
    | undefined
  > {
    this.changeset.execute();
    const payload = this.computePayload();
    const patient = payload.patient as Patient;
    delete payload.patient;
    const attrs = Object.fromEntries(
      Object.entries(payload).map(([k, v]) => [dasherize(k), v])
    );

    const body = JSON.stringify({
      data: {
        "journal-event": {
          data: {
            type: `${journalEventClassType}s`,
            attributes: attrs,
            relationships: {
              patient: {
                data: {
                  id: patient.id
                }
              }
            }
          }
        },
        "file-attachments": (this.changeset.get("files") as File[]).map(file =>
          file.fileAttachment.serialize()
        ),
        "file-contents": (this.changeset.get("files") as File[]).map(file =>
          file.fileContent.serialize()
        )
      }
    });

    let journalEvent;

    const { response, content } = await this.requestManager.request({
      url: "/api/v1/journal_event_with_attachment",
      method: "POST",
      body: body
    });

    if (!response.ok) {
      emberSwal.fire({
        title: "Oups, échec de l'importation de votre document.",
        text: "Veuillez réessayer ou contacter le support Sêmeia si le problème persiste.",
        icon: "error"
      });
    } else {
      // Fetch saved record
      journalEvent = await this.store.findRecord(
        content.data.type.slice(0, -1),
        content.data.id,
        {
          include: "file-attachments"
        }
      );
    }

    return journalEvent;
  }

  async save(): Promise<typeof this.journalEventClass | undefined> {
    if (this.changeset.get("files")) {
      return (await this.saveWithFiles()) as typeof this.journalEventClass;
    }

    if (!this.isDirty) {
      return;
    }
    let savedModel;

    this.changeset.execute();
    if (this.formAction === FormAction.Edit) {
      const sourceJournalEvent = this.retrievedJournalEvent;
      if (sourceJournalEvent) {
        await sourceJournalEvent.archive();
      }

      savedModel = await this.saveModel();

      if (sourceJournalEvent && sourceJournalEvent.fileAttachments.length > 0)
        await this.transferFileAttachments(
          sourceJournalEvent.fileAttachments,
          savedModel
        );
    } else {
      savedModel = await this.saveModel();
    }

    await this.draft.onDidSave();
    this.popToast();
    return savedModel;
  }

  popToast() {
    if (this.toastMessage)
      this.flashMessages.success(this.toastMessage.message, {
        title: this.toastMessage.title
      });
  }

  /**
   * A payload based on the `journalEventProperties`
   */
  computePayload(): Payload {
    return this.journalEventProperties.reduce(
      (acc, property) => {
        acc[property] = this.model[property];
        return acc;
      },
      { patient: this.patient }
    );
  }

  async saveModel(): Promise<typeof this.journalEventClass> {
    // TODO: Create the changeset from a new JournalEvent and then save it
    // Think about removing all the JE with 'isNew' from the store.

    const payload = this.computePayload();

    const journalEventRecord = this.store.createRecord(
      this.journalEventClassType,
      payload
    );

    const newJe = await journalEventRecord.save();

    return newJe;
  }

  async transferFileAttachments(
    fileAttachments: NonNullable<Payload["fileAttachments"]>,
    newJe: JournalEventModel
  ): Promise<void> {
    await Promise.all(
      fileAttachments.slice().map(async f => {
        // @ts-expect-error journalEvent is declared as AsyncBelongsTo.
        //   When settings the value, from TypeScript point-of-view,
        //   a promise should be set, while setting the value directly
        //   is actually supported  by Ember Data, and is the official way.
        f.journalEvent = newJe;
        await f.save();
      })
    );
  }
}
